/**
 * Teleconsultation Consent Public Landing Page (/tele-consent/:token).
 * R39 M18.b (SO-P268, SO-D229, SO-D236, Fix1 F1).
 * Zero patient data (SO-D229): shows clinic name, scheduled time, consent version only.
 */

import React, { useState, useEffect, useCallback } from 'react'
import type { ConfirmationResult } from 'firebase/auth'
import { Button } from '../Common/Button'
import { TextInput } from '../Common/TextInput'
import { useNls } from '../../nls/nlsProvider'
import { getApiErrorMessage } from '../../services/apiConnectivity'
import { sendOutsidePhoneOtp } from '../../services/outsidePhoneOtp'
import { getApprovalsAuth, signOutApprovals } from '../../services/approvalsApp'
import {
  getTeleSessionPublicInfo,
  prepareTeleConsentOtp,
  submitTeleConsent,
} from '../../services/opsClinicsService'
import type { GetTeleSessionPublicInfoResult } from '../../types/ops'
import './teleConsentLanding.css'

export interface TeleConsentLandingProps {
  token: string
}

type Step = 'LOADING' | 'PHONE_ENTRY' | 'OTP_ENTRY' | 'SUCCESS' | 'INVALID'

export const TeleConsentLanding: React.FC<TeleConsentLandingProps> = ({ token }) => {
  const { t } = useNls()

  const [step, setStep] = useState<Step>('LOADING')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const [info, setInfo] = useState<GetTeleSessionPublicInfoResult | null>(null)
  const [phone, setPhone] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null)

  const fetchPublicInfo = useCallback(async () => {
    if (!token) {
      setErrorMsg(t('NLSOpsClinicsTeleExpired'))
      setStep('INVALID')
      return
    }
    setStep('LOADING')
    setErrorMsg(null)
    try {
      const res = await getTeleSessionPublicInfo({ token })
      setInfo(res)
      setStep('PHONE_ENTRY')
    } catch (err: unknown) {
      setErrorMsg(getApiErrorMessage(err))
      setStep('INVALID')
    }
  }, [token, t])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchPublicInfo()
  }, [fetchPublicInfo])

  const handleSendOtp = async () => {
    if (!phone.trim()) return
    setSubmitting(true)
    setErrorMsg(null)
    try {
      const formattedPhone = phone.trim().startsWith('+') ? phone.trim() : `+${phone.trim()}`
      const prep = await prepareTeleConsentOtp({
        token,
        phone: formattedPhone,
      })
      if (!prep.success || prep.attemptsRemaining <= 0) {
        setErrorMsg(t('NLSOpsClinicsTeleExpired'))
        setSubmitting(false)
        return
      }

      const confirmation = await sendOutsidePhoneOtp(getApprovalsAuth(), formattedPhone)
      setConfirmationResult(confirmation)
      setStep('OTP_ENTRY')
    } catch (err: unknown) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmitConsent = async () => {
    if (!confirmationResult || !otpCode.trim() || !agreed) return
    setSubmitting(true)
    setErrorMsg(null)
    try {
      await confirmationResult.confirm(otpCode.trim())
      await submitTeleConsent({
        token,
        agreed: true,
      })
      await signOutApprovals()
      setStep('SUCCESS')
    } catch (err: unknown) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="teleConsent">
      <div className="teleConsent__card">
        <header className="teleConsent__header">
          <h1 className="teleConsent__title">
            {t('NLSOpsClinicsTeleConsentLandingTitle')}
          </h1>
          <p className="teleConsent__subtitle">
            {t('NLSOpsClinicsTeleConsentLandingSubtitle')}
          </p>
        </header>

        {errorMsg && <div className="teleConsent__error">{errorMsg}</div>}

        {step === 'LOADING' ? (
          <div className="teleConsent__loading" role="status" />
        ) : step === 'SUCCESS' ? (
          <div className="teleConsent__success">
            {t('NLSOpsClinicsTeleConsentLandingSuccess')}
          </div>
        ) : (
          info && (
            <>
              <div className="teleConsent__meta">
                <div className="teleConsent__metaRow">
                  <span className="teleConsent__metaLabel">
                    {t('NLSOpsClinicsTeleSessions')}
                  </span>
                  <span className="teleConsent__metaValue">{info.clinicName}</span>
                </div>
                <div className="teleConsent__metaRow">
                  <span className="teleConsent__metaLabel">
                    {t('NLSOpsTabClinicsTele')}
                  </span>
                  <span className="teleConsent__metaValue">
                    {new Date(info.scheduledAt).toLocaleString()}
                  </span>
                </div>
                {info.phoneHint && (
                  <div className="teleConsent__metaRow">
                    <span className="teleConsent__metaLabel">
                      {t('NLSOpsClinicsTeleSendLink')}
                    </span>
                    <span className="teleConsent__metaValue">{info.phoneHint}</span>
                  </div>
                )}
              </div>

              {step === 'PHONE_ENTRY' && (
                <>
                  <div className="teleConsent__inputGroup">
                    <label className="teleConsent__metaLabel">
                      {t('NLSOpsClinicsTeleSendLink')}
                    </label>
                    <TextInput
                      placeholder="+91 98765 43210"
                      value={phone}
                      onChange={(val) => setPhone(val)}
                    />
                  </div>

                  <Button
                    variant="primary"
                    onClick={handleSendOtp}
                    disabled={submitting || !phone.trim()}
                    loading={submitting}
                  >
                    {t('NLSOpsClinicsTeleConsentLandingSendOtp')}
                  </Button>
                </>
              )}

              {step === 'OTP_ENTRY' && (
                <>
                  <div className="teleConsent__inputGroup">
                    <label className="teleConsent__metaLabel">
                      {t('NLSOpsClinicsTeleConsentLandingVerifyOtp')}
                    </label>
                    <TextInput
                      placeholder="••••••"
                      value={otpCode}
                      onChange={(val) => setOtpCode(val)}
                    />
                  </div>

                  <label className="teleConsent__checkboxLabel">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                    />
                    {t('NLSOpsClinicsTeleConsentLandingTick')}
                  </label>

                  <Button
                    variant="primary"
                    onClick={handleSubmitConsent}
                    disabled={submitting || !agreed || !otpCode.trim()}
                    loading={submitting}
                  >
                    {t('NLSOpsClinicsTeleConsentLandingSubmit')}
                  </Button>
                </>
              )}
            </>
          )
        )}
      </div>
    </div>
  )
}
